<!--
  Disables any element when the market is closed, displaying a tooltip.
  If the market is open, forwards all props and slots.
  Used via wrapper components (AuroraBtn, AuroraBtnDropdown, AuroraListItem).
-->
<script setup lang="ts">
import { type PropType, computed } from 'vue';
import { store as vuex } from '@/store/store';
import { isFeatureEnabled } from '@/modules/market-closed/helpers/market-closed';
import type { TimeframeKey } from '@/modules/market-closed/types/market-closed';
import type { ClientConfig, UXConfig } from '@/utils/helpers/rest';
import { useCurrentTime } from '@/composables/current-time';

const props = defineProps({
  // Valid values: maintenanceWindow | createLoans | rollLoans | settleLoans |
  // cancelLoans | recallLoans | buyInLoans | rateModLoans
  timeframe: {
    type: String as PropType<TimeframeKey>,
    default: undefined,
  },
  tooltip: {
    type: String as PropType<string | null>,
    default: null,
  },
  componentName: {
    type: [String, Object, Function] as PropType<string | object>,
    required: true,
  },
});

const emit = defineEmits<{
  (e: 'click', event: MouseEvent): void;
}>();

const { currentTimeUTC } = useCurrentTime();
const clientConfig = computed<ClientConfig | null>(() => vuex.state.clientConfig);
const uxConfig = computed<UXConfig>(() => vuex.state.uxConfig);

const tooltips: Record<TimeframeKey, string> = {
  maintenanceWindow: 'market is currently closed',
  createLoans: 'market is currently closed',
  rollLoans: 'market is currently closed',
  settleLoans: 'market is currently closed',
  cancelLoans: 'currently unavailable',
  recallLoans: 'currently unavailable',
  buyInLoans: 'currently unavailable',
  rateModLoans: 'currently unavailable',
};

// Determines if the market is closed for the feature defined by the provided timeframe key
const isTimeframeDisabled = computed(() =>
  props.timeframe
    ? !isFeatureEnabled(props.timeframe, clientConfig.value, uxConfig.value, currentTimeUTC.value)
    : false
);

const timeframeMessage = computed(
  () => tooltips[props.timeframe!] ?? 'feature is currently disabled'
);

const cssClass = computed(() => ['wrapped-component']);
</script>

<template>
  <VTooltip v-if="isTimeframeDisabled || tooltip" nudge-top="3" top>
    <template #activator="{ on, attrs }">
      <div class="activator" v-bind="attrs" v-on="on">
        <component
          :is="componentName"
          v-bind="$attrs"
          :class="cssClass"
          :disabled="isTimeframeDisabled || $attrs.disabled"
        >
          <template v-for="(index, name) in $slots" #[name]>
            <slot :name="name" />
          </template>
        </component>
      </div>
    </template>
    <span>{{ tooltip ?? timeframeMessage }}</span>
  </VTooltip>

  <!-- no need for tooltip/disable: just forward everything -->
  <component
    :is="componentName"
    v-else
    v-bind="$attrs"
    @click="!$attrs.disabled && emit('click', $event)"
  >
    <template v-for="(index, name) in $slots" #[name]>
      <slot :name="name" />
    </template>
  </component>
</template>

<style lang="scss" scoped>
::v-deep {
  .wrapped-component {
    pointer-events: all;
    cursor: no-drop;
  }
}
</style>
